// A typed state or province as a Near You area (feedback #42, 2026-10-02).
// "ZIP, city, or state" sent a state to its centre point and listed places
// within 50 miles of it, so "Illinois" showed nothing around Chicago. A
// state now lists every place in that state. Pure: used by the server's
// /api/near filter and the app's text search.

export const US_STATES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  PR: 'Puerto Rico',
};

const NAME_TO_CODE = Object.fromEntries(
  Object.entries(US_STATES).map(([code, name]) => [name.toLowerCase(), code])
);

// Lower case, single spaces, accents folded ("Québec" is "quebec").
const norm = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Every spelling a region goes by, lower case: a US state answers to its
// name and its two-letter code; anywhere else to the names given.
export function regionNames(...names) {
  const out = new Set();
  for (const raw of names) {
    const n = norm(raw);
    if (!n) continue;
    out.add(n);
    const code = NAME_TO_CODE[n];
    if (code) out.add(code.toLowerCase());
    const name = US_STATES[n.toUpperCase()];
    if (n.length === 2 && name) out.add(name.toLowerCase());
  }
  return out;
}

// Does an address name one of these? A full name as whole words; a
// two-letter code only where an address puts it ("..., IL" or "IL 60606"),
// so the word "in" is not Indiana.
function mentions(text, names) {
  const raw = norm(text);
  if (!raw) return false;
  const words = ` ${raw.replace(/[,.;()]/g, ' ').replace(/\s+/g, ' ')} `;
  for (const n of names) {
    if (n.length === 2) {
      const c = escapeRe(n);
      if (new RegExp(`(^|,)\\s*${c}\\s*(\\d{5}(-\\d{4})?)?\\s*(,|$)`).test(raw)) return true;
      if (new RegExp(`\\s${c}\\s+\\d{5}`).test(raw)) return true;
    } else if (withoutLonger(words, n).includes(` ${n} `)) {
      return true;
    }
  }
  return false;
}

const ALL_US = regionNames(...Object.keys(US_STATES));
const US_NAMES = [...ALL_US].filter((n) => n.length > 2);
const US_CODES = [...ALL_US].filter((n) => n.length === 2);

// "west virginia" is not Virginia.
function withoutLonger(words, n) {
  let out = words;
  for (const m of US_NAMES) {
    if (m !== n && ` ${m} `.includes(` ${n} `)) out = out.split(` ${m} `).join(' ');
  }
  return out;
}

// The one US state code an address puts in the state's place, or null. It
// decides over a name elsewhere in the text: "Kansas City, MO" is Missouri.
function addressCode(text) {
  const found = US_CODES.filter((c) => mentions(text, [c]));
  return found.length === 1 ? found[0] : null;
}

const COUNTRY = new Set([
  'usa',
  'us',
  'u.s.',
  'u.s.a.',
  'united states',
  'united states of america',
]);

// A US state written out where an address puts it: the last part after a
// comma, with or without a ZIP ("..., Chicago, Illinois 60611"). A street
// named after a state ("520 N Michigan Ave, Chicago") is not one.
function addressStateName(text) {
  const parts = norm(text)
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  // The suggestions save "Street, City, State, 53142": the ZIP is its own part.
  const tail = (p) => COUNTRY.has(p) || /^\d{5}(-\d{4})?$/.test(p);
  while (parts.length > 1 && tail(parts[parts.length - 1])) parts.pop();
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
  return US_NAMES.includes(last) ? last : null;
}

// Is this place in the area? Its stored region decides. A place with no
// region (a store picked from the suggestions is saved without one) is in
// unless its address puts a different US state in the state's place: the
// box overlaps the neighbours, and a store just over the border says so in
// its address.
export function placeInRegion(place, area) {
  const names = regionNames(area?.name, area?.code);
  if (!names.size) return false;
  const r = norm(place?.region);
  if (r) return [...regionNames(r)].some((v) => names.has(v));
  const code = addressCode(place?.address);
  if (code) return names.has(code);
  const state = addressStateName(place?.address);
  return state ? names.has(state) : true;
}

// The Near You text box: name, city, address or the place's state, where
// a state matches by name or code ("Illinois" finds a place saved as IL).
export function placeMatchesText(place, query) {
  const q = norm(query);
  if (!q) return true;
  for (const f of [place?.name, place?.city, place?.address, place?.region]) {
    if (norm(f).includes(q)) return true;
  }
  const asState = regionNames(q);
  if (asState.size > 1) {
    const r = norm(place?.region);
    if (r && [...regionNames(r)].some((v) => asState.has(v))) return true;
    if (!r && mentions(place?.address, asState)) return true;
  }
  return false;
}
